import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchVisualChartData } from "../../../store/slices/visualSlice";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "./DepartmentChart.css";
import { Loading } from "../../common";
import { useTheme } from "../../../context/ThemeContext";

const ITEMS_PER_PAGE = 5;

const COLORS = {
  safe: "#10b981",      // emerald green
  low: "#06b6a4",       // teal
  medium: "#f59e0b",    // amber
  high: "#fb7185",      // light rose
  critical: "#f43f5e",  // deep rose
};

const getRiskColor = (score) => {
  if (score === 0) return COLORS.safe;
  if (score > 0 && score <= 30) return COLORS.low;
  if (score > 30 && score <= 60) return COLORS.medium;
  if (score > 60 && score <= 90) return COLORS.high;
  return COLORS.critical; // > 90
};

const getRiskLabel = (score) => {
  if (score === 0) return "Safe";
  if (score > 0 && score <= 30) return "Low";
  if (score > 30 && score <= 60) return "Medium";
  if (score > 60 && score <= 90) return "High";
  return "Critical"; // > 90
};

export default function RiskDashboard() {
  const dispatch = useDispatch();
  const { chartDataPayload, isChartLoading, chartError } = useSelector(
    (state) => state.visuals || {}
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterLevel, setFilterLevel] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const { isDark } = useTheme();

  useEffect(() => {
    dispatch(fetchVisualChartData());
  }, [dispatch]);

  const rawData = chartDataPayload?.data || [];
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, filterLevel]);

  const stats = useMemo(() => {
    if (!rawData.length) {
      return { avgScore: 0, totalHumans: 0, byLevel: {} };
    }

    const totalHumans = rawData.reduce((sum, d) => sum + (d.total_humans || 0), 0);
    const avgScore = Math.round(
      rawData.reduce((sum, d) => sum + (d.global_posture_score || 0), 0) / rawData.length
    );

    const byLevel = { safe: 0, low: 0, medium: 0, high: 0, critical: 0 };
    rawData.forEach((item) => {
      const s = item.global_posture_score;
      if (s === 0) byLevel.safe++;
      else if (s > 0 && s <= 30) byLevel.low++;
      else if (s > 30 && s <= 60) byLevel.medium++;
      else if (s > 60 && s <= 90) byLevel.high++;
      else byLevel.critical++;
    });

    return { avgScore, totalHumans, byLevel };
  }, [rawData]);

  const filteredData = useMemo(() => {
    let data = rawData.map((item) => ({
      name: item.department ? item.department.trim() : "Unknown",
      score: parseFloat((item.global_posture_score || 0).toFixed(1)),
      riskScore: item.risk_score,
      totalHumans: item.total_humans,
    }));

    if (searchTerm.trim()) {
      data = data.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterLevel !== "all") {
      data = data.filter((item) => {
        if (filterLevel === "safe") return item.score === 0;
        if (filterLevel === "low") return item.score > 0 && item.score <= 30;
        if (filterLevel === "medium") return item.score > 30 && item.score <= 60;
        if (filterLevel === "high") return item.score > 60 && item.score <= 90;
        if (filterLevel === "critical") return item.score > 90;
        return true;
      });
    }

    data.sort((a, b) =>
      sortOrder === "desc" ? b.score - a.score : a.score - b.score
    );

    return data;
  }, [rawData, searchTerm, sortOrder, filterLevel]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  const chartOptions = useMemo(() => {
    const categories = paginatedData.map((d) => d.name);
    const seriesData = paginatedData.map((d) => ({
      y: d.score,
      color: getRiskColor(d.score),
      riskScore: d.riskScore,
      totalHumans: d.totalHumans,
    }));

    return {
      chart: {
        type: "bar",
        height: 280,
        backgroundColor: "transparent",
        style: { fontFamily: "inherit" },
        spacing: [5, 10, 5, 5],
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: categories,
        labels: {
          style: { fontSize: "11px", color: "#475569", fontWeight: "500" },
        },
        lineColor: "#e2e8f0",
        tickColor: "#e2e8f0",
      },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: null },
        labels: {
          format: "{value}%",
          style: { fontSize: "10px", color: "#94a3b8" },
        },
        gridLineColor: "transparent",
        plotLines: [
          {
            value: 60,
            color: COLORS.medium,
            dashStyle: "Dash",
            width: 1,
            zIndex: 3,
          },
          {
            value: 90,
            color: COLORS.critical,
            dashStyle: "Dash",
            width: 1,
            zIndex: 3,
          },
        ],
      },
      tooltip: {
        backgroundColor: isDark ? "#1b3157" : "#ffffff",
        borderColor: isDark ? "#233554" : "#e2e8f0",
        borderWidth: 1,
        borderRadius: 8,
        shadow: { color: "rgba(0,0,0,0.08)", offsetX: 0, offsetY: 3, width: 10 },
        useHTML: true,
        formatter: function () {
          const point = this.point;
          return `
            <div style="padding: 4px;">
              <div style="font-weight: 700; color: ${isDark ? "#E6F1FF" : "#1e293b"}; margin-bottom: 4px; font-size: 12px;">${this.x}</div>
              <div style="font-size: 11px; color:${isDark ? "#a1a3a9" : "#64748b"}; line-height: 1.5;">
                Posture: <b style="color: ${point.color};">${this.y}%</b> (${getRiskLabel(this.y)})<br/>
                Team Size: <b style="color:${isDark ? "#E6F1FF" : "#1e293b"};">${point.totalHumans} humans</b>
              </div>
            </div>
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.05,
          dataLabels: {
            enabled: true,
            inside: true,
            align: "center",
            verticalAlign: "middle",
            format: "{y}%",
            style: {
              fontSize: "15px",
              fontWeight: "800",
              color: "#ffffff",
              textOutline: "none",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            },
            crop: false,
            overflow: "allow",
          },
        },
      },
      series: [{ name: "Posture Score", data: seriesData }],
    };
  }, [paginatedData, isDark]);

  if (isChartLoading) {
    return (
      <div className="risk-compact-card">
        <Loading text="Loading..."/>
      </div>
    );
  }

  if (chartError) {
    return (
      <div className="risk-compact-card">
        <div className="risk-state-message risk-state-error">Error: {chartError}</div>
      </div>
    );
  }

  if (!rawData.length) {
    return (
      <div className="risk-compact-card">
        <div className="risk-state-message">No data available</div>
      </div>
    );
  }

  return (
    <div className="risk-compact-card">
      <div className="risk-header">
        <div>
          <h3 className="risk-title">Department Risk Posture</h3>
          <p className="risk-subtitle">
            {rawData.length} departments · {stats.totalHumans.toLocaleString()} humans · Higher = More Critical
          </p>
        </div>
        <div className="risk-overall-badge">
          <span className="risk-overall-value">{rawData.length}</span>
          <span className="risk-overall-label">Total Departments</span>
        </div>
      </div>

      <div className="risk-main-content">
  {/* NEW WRAPPER TO KEEP THE CHART & FILTERS SIDE-BY-SIDE */}
  <div className="risk-body-wrapper">
    
    <div className="risk-chart-section">
      {paginatedData.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      ) : (
        <div className="risk-state-message">No matches</div>
      )}
    </div>

    <div className="risk-filters-section">
      <div className="risk-search-wrapper">
        <svg className="risk-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="risk-search-input"
        />
        {searchTerm && (
          <button className="risk-clear-btn" onClick={() => setSearchTerm("")}>×</button>
        )}
      </div>

      <button
        className="risk-sort-btn"
        onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
      >
        {sortOrder === "desc" ? "Highest ↓" : "Lowest ↑"}
      </button>

      <div className="risk-filter-pills">
        <button
          className={`risk-pill ${filterLevel === "all" ? "active" : ""}`}
          onClick={() => setFilterLevel("all")}
        >
          <span className="risk-pill-count">{rawData.length}</span>
          <span className="risk-pill-label">All</span>
        </button>
        <button
          className={`risk-pill risk-pill-safe ${filterLevel === "safe" ? "active" : ""}`}
          onClick={() => setFilterLevel("safe")}
          title="= 0%"
        >
          <span className="risk-pill-count">{stats.byLevel.safe}</span>
          <span className="risk-pill-label">Safe</span>
        </button>
        <button
          className={`risk-pill risk-pill-low ${filterLevel === "low" ? "active" : ""}`}
          onClick={() => setFilterLevel("low")}
          title="0-30%"
        >
          <span className="risk-pill-count">{stats.byLevel.low}</span>
          <span className="risk-pill-label">Low</span>
        </button>
        <button
          className={`risk-pill risk-pill-medium ${filterLevel === "medium" ? "active" : ""}`}
          onClick={() => setFilterLevel("medium")}
          title="30-60%"
        >
          <span className="risk-pill-count">{stats.byLevel.medium}</span>
          <span className="risk-pill-label">Medium</span>
        </button>
        <button
          className={`risk-pill risk-pill-high ${filterLevel === "high" ? "active" : ""}`}
          onClick={() => setFilterLevel("high")}
          title="60-90%"
        >
          <span className="risk-pill-count">{stats.byLevel.high}</span>
          <span className="risk-pill-label">High</span>
        </button>
        <button
          className={`risk-pill risk-pill-critical ${filterLevel === "critical" ? "active" : ""}`}
          onClick={() => setFilterLevel("critical")}
          title=">90%"
        >
          <span className="risk-pill-count">{stats.byLevel.critical}</span>
          <span className="risk-pill-label">Critical</span>
        </button>
      </div>
    </div>

  </div> {/* END OF RISK BODY WRAPPER */}

  {/* PAGINATION MOVED OUT HERE TO SPAN THE WHOLE CONTENT WIDTH */}
  {totalPages > 1 && paginatedData.length > 0 && (
    <div className="risk-pagination">
      <button
        className="risk-page-btn"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        ←
      </button>
      <span className="risk-page-info">
        {currentPage} / {totalPages}
      </span>
      <button
        className="risk-page-btn"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    </div>
  )}
</div>
    </div>
  );
}